import { MutationFunctionOptions } from '@apollo/client/react/types/types';
import { useToast } from '@chakra-ui/react';
import {
  useInternalCreateOrUpdateIngestionDocumentValidationFeedbackMutation,
  useInternalGetIngestionDocumentValidationFeedbackQuery,
} from '@client/graphql/__generated__/document-operations';
import {
  useExternalCreateOrUpdateIngestionDocumentValidationFeedbackMutation,
  useExternalGetIngestionDocumentValidationFeedbackQuery,
} from '@client/graphql/__generated__/main-operations';
import { FeedbackType } from '@client/graphql/__generated__/types';
import { useCallback, useMemo } from 'react';
import { ComplianceValidationVariant } from '../ComplianceDrawer';

type UseComplianceFeedbackProps = {
  firstIngestionDocumentValidationId: string | undefined;
  variant: ComplianceValidationVariant;
};

export const useComplianceFeedback = ({
  firstIngestionDocumentValidationId,
  variant,
}: UseComplianceFeedbackProps) => {
  const toast = useToast();

  const isExternal = variant === 'external';

  // External feedback query
  const { data: externalFeedbackData, refetch: refetchExternalFeedback } =
    useExternalGetIngestionDocumentValidationFeedbackQuery({
      variables: {
        input: {
          ingestionDocumentValidationId: firstIngestionDocumentValidationId!,
        },
      },
      skip: !firstIngestionDocumentValidationId || !isExternal,
    });

  // Internal feedback query
  const { data: internalFeedbackData, refetch: refetchInternalFeedback } =
    useInternalGetIngestionDocumentValidationFeedbackQuery({
      variables: {
        input: {
          ingestionDocumentValidationId: firstIngestionDocumentValidationId!,
        },
      },
      skip: !firstIngestionDocumentValidationId || isExternal,
    });

  const allFeedback = useMemo(() => {
    if (isExternal) {
      return (
        externalFeedbackData?.externalGetIngestionDocumentValidationFeedback ||
        []
      );
    } else {
      return (
        internalFeedbackData?.internalGetIngestionDocumentValidationFeedback ||
        []
      );
    }
  }, [externalFeedbackData, internalFeedbackData, isExternal]);

  const handleFeedbackUpdate = useCallback(() => {
    if (isExternal) {
      void refetchExternalFeedback();
    } else {
      void refetchInternalFeedback();
    }
  }, [refetchExternalFeedback, refetchInternalFeedback, isExternal]);

  const [
    createOrUpdateExternalFeedback,
    { loading: createOrUpdateExternalFeedbackLoading },
  ] = useExternalCreateOrUpdateIngestionDocumentValidationFeedbackMutation();

  const [
    createOrUpdateInternalFeedback,
    { loading: createOrUpdateInternalFeedbackLoading },
  ] = useInternalCreateOrUpdateIngestionDocumentValidationFeedbackMutation();

  const createOrUpdateFeedback = useCallback(
    (
      feedback: {
        ingestionDocumentValidationId: string;
        ruleUuid: string;
        contractDocumentUuid: string;
        feedbackType: FeedbackType;
        comment?: string;
      },
      onFinish?: () => void
    ) => {
      const toastOptions: MutationFunctionOptions = {
        onCompleted: () => {
          toast({
            title: 'Feedback submitted',
            description: 'Thanks for submitting feedback!',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          handleFeedbackUpdate();
          onFinish?.();
        },
        onError: (error) => {
          toast({
            title: 'Error submitting feedback',
            description: error.message,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        },
      };

      if (isExternal) {
        void createOrUpdateExternalFeedback({
          variables: { input: feedback },
          onCompleted: toastOptions.onCompleted,
          onError: toastOptions.onError,
        });
      } else {
        void createOrUpdateInternalFeedback({
          variables: { input: feedback },
          onCompleted: toastOptions.onCompleted,
          onError: toastOptions.onError,
        });
      }
    },
    [
      createOrUpdateExternalFeedback,
      createOrUpdateInternalFeedback,
      handleFeedbackUpdate,
      isExternal,
      toast,
    ]
  );

  return {
    allFeedback,
    createOrUpdateFeedback,
    createOrUpdateFeedbackLoading: isExternal
      ? createOrUpdateExternalFeedbackLoading
      : createOrUpdateInternalFeedbackLoading,
  };
};
